import { REQUEST_OUTCOMES, requestTransport } from './helpers/requestTransport';
import redirectToLogin from './helpers/redirectToLogin';

// Authenticator-app (TOTP) sign-in settings for the signed-in user.
//
// These thunks call the transport directly instead of going through `crud`:
// `crud` dispatches the raw axios response/error, and those carry the request
// body (the password or the code) and, for setup, the new TOTP secret. Only the
// resulting enabled-at timestamp reaches the store.

export const AUTHENTICATOR_APP = {
  UPDATED: 'AUTHENTICATOR_APP_UPDATED',
};

const ENDPOINT = '/api/v1/authenticator_app';
const FALLBACK_ERROR = 'Something went wrong. Please try again.';
const RATE_LIMITED_ERROR = 'Too many attempts. Please wait a few minutes and try again.';

// Keeps only the status and the server's message. The default normalizer
// serializes the whole axios error, request body included.
const toFailure = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.error;
  if (typeof message === 'string' && message.trim()) return { status, error: message };
  return { status, error: status === 429 ? RATE_LIMITED_ERROR : FALLBACK_ERROR };
};

const send = async (dispatch, options) => {
  const result = await requestTransport(options, {
    // Confirm and remove commit before they respond; replaying them after a 503
    // would surface "Setup expired" for a setup that actually succeeded.
    retryOnUnavailable: false,
    normalizeError: toFailure,
    // A wrong password is a 422, so a 401 here really is an expired session.
    onUnauthorized: (error) => redirectToLogin(dispatch, error),
  });

  if (result.outcome === REQUEST_OUTCOMES.success) {
    return { data: result.value?.data || {} };
  }
  if (result.outcome === REQUEST_OUTCOMES.cancelled) {
    return { error: FALLBACK_ERROR, cancelled: true };
  }
  return result.value;
};

const enabledAtUpdated = (authenticatorAppEnabledAt) => ({
  type: AUTHENTICATOR_APP.UPDATED,
  payload: { authenticatorAppEnabledAt: authenticatorAppEnabledAt || null },
});

// Returns `{ data: { secret, otpauth_uri, qr_code, expires_in } }`. The secret is
// handed back to the caller only; it is never dispatched or stored.
export const setupAuthenticatorApp = (password) => (dispatch) => send(dispatch, {
  method: 'POST',
  url: `${ENDPOINT}/setup`,
  data: { password },
});

export const confirmAuthenticatorApp = (code) => async (dispatch) => {
  const result = await send(dispatch, {
    method: 'POST',
    url: `${ENDPOINT}/confirm`,
    // `otp_code`, not `code`: the server's log filter redacts OTP keys by name.
    data: { otp_code: code },
  });
  if (!result.error) dispatch(enabledAtUpdated(result.data.authenticator_app_enabled_at));
  return result;
};

export const removeAuthenticatorApp = (password) => async (dispatch) => {
  const result = await send(dispatch, {
    method: 'DELETE',
    url: ENDPOINT,
    // axios sends `data` as the DELETE request body.
    data: { password },
  });
  if (!result.error) dispatch(enabledAtUpdated(null));
  return result;
};
